import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Card,
} from "react-bootstrap";
import {
  Users,
  Search,
  Edit,
  Trash2,
  RefreshCcw,
  UserCircle,
  Phone,
  Mail,
  Wallet,
  ShieldCheck,
} from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";

// Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const API_URL = "https://beaconsms.com/oldsite/api/manage_members_api.php";

const ManageMembers = () => {
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.id === "71";

  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchMembers = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?adminId=71`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch error", err);
      Swal.fire("Error", "Failed to load member list.", "error");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    document.title = "Manage Members - beaconSMS";
    fetchMembers();
  }, [fetchMembers]);

  // Search Logic: Filters by Username, Name, Phone, or Email
  const filteredMembers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return members.filter(
      (m) =>
        (m.username || "").toLowerCase().includes(term) ||
        (m.name || "").toLowerCase().includes(term) ||
        (m.phone || "").includes(term) ||
        (m.email || "").toLowerCase().includes(term),
    );
  }, [members, searchTerm]);

  const handleDelete = (userId, username) => {
    if (userId === "71") {
      return Swal.fire(
        "Action Blocked",
        "You cannot delete the primary admin account.",
        "error",
      );
    }

    Swal.fire({
      title: "Confirm Deletion",
      text: `Are you sure you want to permanently delete @${username}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete Member",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.post(API_URL, {
            adminId: 71,
            action: "delete",
            userId,
          });
          if (res.data.status === "success") {
            Swal.fire(
              "Deleted!",
              "Member has been removed from the system.",
              "success",
            );
            fetchMembers();
          } else {
            Swal.fire("Error", res.data.message, "error");
          }
        } catch (err) {
          Swal.fire("Error", "Server connection failed.", "error");
        }
      }
    });
  };

  const handleEdit = (m) => {
    Swal.fire({
      title: `Update @${m.username}`,
      html: `
        <div style="text-align: left; font-family: sans-serif;">
          <div class="mb-2">
            <label style="font-size: 12px; font-weight: bold; color: #666;">Username</label>
            <input id="swal-username" class="swal2-input" style="margin-top: 5px;" placeholder="Username" value="${m.username || ""}">
          </div>
          <div class="mb-2">
            <label style="font-size: 12px; font-weight: bold; color: #666;">Full Name</label>
            <input id="swal-name" class="swal2-input" style="margin-top: 5px;" placeholder="Full Name" value="${m.name || ""}">
          </div>
          <div class="mb-2">
            <label style="font-size: 12px; font-weight: bold; color: #666;">Email Address</label>
            <input id="swal-email" class="swal2-input" style="margin-top: 5px;" placeholder="Email" value="${m.email || ""}">
          </div>
          <div class="mb-2">
            <label style="font-size: 12px; font-weight: bold; color: #666;">Phone Number</label>
            <input id="swal-phone" class="swal2-input" style="margin-top: 5px;" placeholder="Phone" value="${m.phone || ""}">
          </div>
          <div class="mb-2">
            <label style="font-size: 12px; font-weight: bold; color: #d9534f;">Wallet Balance (Units)</label>
            <input id="swal-wallet" type="number" step="0.01" class="swal2-input" style="margin-top: 5px; border-color: #d9534f;" placeholder="Wallet" value="${m.wallet || 0}">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Save Changes",
      confirmButtonColor: "#0d6efd",
      preConfirm: () => {
        return {
          username: document.getElementById("swal-username").value,
          name: document.getElementById("swal-name").value,
          email: document.getElementById("swal-email").value,
          phone: document.getElementById("swal-phone").value,
          wallet: document.getElementById("swal-wallet").value,
        };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.post(API_URL, {
            adminId: 71,
            action: "update",
            userId: m.id,
            ...result.value,
          });

          if (res.data.status === "success") {
            Swal.fire(
              "Success!",
              "User record and balance updated.",
              "success",
            );
            fetchMembers();
          } else {
            Swal.fire("Failed", res.data.message, "error");
          }
        } catch (err) {
          Swal.fire("Error", "Communication failure with the API.", "error");
        }
      }
    });
  };

  if (authLoading)
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
      </div>
    );

  if (!isAdmin) {
    return (
      <Container className='mt-5 text-center p-5 bg-white border rounded shadow-sm'>
        <ShieldCheck size={64} className='text-danger mb-3' />
        <h2 className='fw-bold'>Unauthorized Access</h2>
        <p className='text-muted'>
          This page is restricted to administrative personnel only.
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        <Col md='9'>
          <div className='bg-white p-4 border rounded shadow-sm min-vh-100'>
            <div className='d-flex justify-content-between align-items-center mb-4 border-bottom pb-3'>
              <div className='d-flex align-items-center text-primary'>
                <Users className='me-2' size={28} />
                <h4 className='mb-0 fw-bold'>Manage Members</h4>
              </div>
              <Button
                variant='link'
                onClick={fetchMembers}
                className='text-decoration-none'
              >
                <RefreshCcw
                  size={20}
                  className={loading ? "fa-spin" : "text-muted"}
                />
              </Button>
            </div>

            <Card className='border-0 bg-light mb-4'>
              <Card.Body className='p-3'>
                <InputGroup>
                  <InputGroup.Text className='bg-white border-end-0'>
                    <Search size={18} className='text-muted' />
                  </InputGroup.Text>
                  <Form.Control
                    className='border-start-0 ps-0'
                    placeholder='Search by username, name, phone or email...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      variant='outline-secondary'
                      onClick={() => setSearchTerm("")}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </InputGroup>
              </Card.Body>
            </Card>

            <div className='table-responsive'>
              <Table hover className='align-middle border rounded shadow-xs'>
                <thead
                  className='bg-light text-uppercase'
                  style={{ fontSize: "12px", letterSpacing: "0.5px" }}
                >
                  <tr>
                    <th>Member Details</th>
                    <th>Contact info</th>
                    <th>Wallet</th>
                    <th className='text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan='4' className='text-center py-5'>
                        <Spinner animation='border' variant='primary' />
                      </td>
                    </tr>
                  ) : filteredMembers.length > 0 ? (
                    filteredMembers.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <UserCircle size={32} className='text-muted me-2' />
                            <div>
                              <div className='fw-bold text-primary'>
                                @{m.username}
                              </div>
                              <div className='small text-muted'>
                                {m.name || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='small mb-1'>
                            <Phone size={12} className='me-1' />{" "}
                            {m.phone || "---"}
                          </div>
                          <div className='small text-muted'>
                            <Mail size={12} className='me-1' />{" "}
                            {m.email || "---"}
                          </div>
                        </td>
                        <td>
                          <Badge
                            bg='success-subtle'
                            className='text-success border border-success-subtle px-3 py-2'
                          >
                            <Wallet size={12} className='me-1' /> {m.wallet}
                          </Badge>
                        </td>
                        <td className='text-end'>
                          <Button
                            variant='outline-primary'
                            size='sm'
                            className='me-2'
                            onClick={() => handleEdit(m)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => handleDelete(m.id, m.username)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='4' className='text-center py-5 text-muted'>
                        No members match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default ManageMembers;
